import { usePdfImport } from "@/contexts/PdfImportContext";
import { usePdfImportJob } from "@/lib/hooks/pdfImport/usePdfImportJob";
import { describeAiError } from "@/lib/utils/aiCredits";
import { Button } from "@ai-teach/ui-web";
import { useApolloClient } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { HiCheckCircle, HiExclamationTriangle, HiMinus, HiXMark } from "react-icons/hi2";

const STAGE_LABEL: Record<string, string> = {
  extracting: "Reading pages…",
  words: "Reading the vocabulary list…",
  generating: "Generating exercises…",
  writing: "Creating slides…",
};

// Overall progress across stages: extraction is the long measurable part (0–70%); the rest are coarse
// checkpoints so the bar keeps advancing instead of sitting at 0 or looking done too early.
function percent(stage: string | null | undefined, done: number, total: number): number {
  if (stage === "extracting") return total > 0 ? Math.round((done / total) * 70) : 5;
  if (stage === "words") return 74;
  if (stage === "generating") return 82;
  if (stage === "writing") return 92;
  return 5;
}

/**
 * App-level floating widget (bottom-right) for the active PDF import. Persists across navigation and the
 * upload modal closing. Minimizes to a pill; expands to a card; on completion toasts and offers a jump
 * to the Project. Rendered once, in the root route.
 */
export function PdfImportTracker() {
  const { active, expanded, expand, minimize, dismiss } = usePdfImport();
  const job = usePdfImportJob(active?.jobId ?? null);
  const client = useApolloClient();
  const navigate = useNavigate();
  const notifiedJobId = useRef<string | null>(null);

  const status = job?.status;
  const done = status === "done";
  const failed = status === "failed";

  // Notify once when a job finishes, and refresh the lessons list (created server-side).
  useEffect(() => {
    if (done && active && notifiedJobId.current !== active.jobId) {
      notifiedJobId.current = active.jobId;
      void client.refetchQueries({ include: "active" });
      toast.success(
        `"${active.fileName}" imported — ${job?.result?.lessonCount ?? 0} lesson(s), ${job?.result?.slideCount ?? 0} slides`
      );
    }
  }, [done, active, job?.result, client]);

  if (!active) return null;

  const pct = done ? 100 : percent(job?.stage, job?.progress.done ?? 0, job?.progress.total ?? 0);
  const showCount = job?.stage === "extracting" && (job?.progress.total ?? 0) > 0;

  const goToProject = () => {
    navigate({ to: "/admin/project/$projectId", params: { projectId: active.parentId } });
    dismiss();
  };

  // ── Minimized pill ──
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={expand}
        className="fixed top-4 right-4 z-[100] flex items-center gap-2 rounded-full bg-slate-800 text-white px-4 py-2 shadow-lg ring-1 ring-gray-700 hover:bg-slate-700 transition-colors"
        title={active.fileName}
      >
        {done ? (
          <HiCheckCircle className="w-5 h-5 text-green-400" />
        ) : failed ? (
          <HiExclamationTriangle className="w-5 h-5 text-red-400" />
        ) : (
          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
        )}
        <span className="text-sm font-medium">
          {done ? "PDF ready" : failed ? "Import failed" : `Importing… ${pct}%`}
        </span>
      </button>
    );
  }

  // ── Expanded card ──
  return (
    <div className="fixed top-4 right-4 z-[100] w-80 panel bg-slate-50 ring-1 ring-gray-700 shadow-xl rounded-lg p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="h6">Import from PDF</p>
          <p className="text-xs text-gray-500 truncate">{active.fileName}</p>
        </div>
        <div className="flex items-center gap-1">
          {!done && !failed && (
            <button
              type="button"
              onClick={minimize}
              aria-label="Minimize"
              className="p-1 text-gray-500 hover:text-secondary-200"
            >
              <HiMinus className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            className="p-1 text-gray-500 hover:text-secondary-200"
          >
            <HiXMark className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mt-3">
        {failed ? (
          <p className="text-red-500 text-sm">{describeAiError(job?.error, "Import failed.")}</p>
        ) : done ? (
          <div className="space-y-3">
            <p className="text-sm text-secondary-200">
              Created {job?.result?.lessonCount ?? 0} lesson(s), {job?.result?.slideCount ?? 0}{" "}
              slides.
            </p>
            <Button variant="primary" onClick={goToProject}>
              Go to project
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="label text-secondary-200">
              {STAGE_LABEL[job?.stage ?? ""] ?? "Starting…"}
              {showCount ? ` (${job?.progress.done}/${job?.progress.total})` : ""}
            </p>
            <div className="w-full h-2 bg-gray-300 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 transition-all"
                style={{ width: `${Math.max(pct, 5)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500">
              This can take a few minutes — you can keep working.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
